import React, { useState } from 'react'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material'

import Tooltip from '@mui/material/Tooltip'
import GoogleIcon from '../../icon/GoogleIcon'
import { METRICS_TABLE_COLUMNS } from '../../poc/PurchaseOrder/constants/metricConstants'
import { resolveChildValue, formatTableValue } from '../../poc/PurchaseOrder/helpers/tableUtils'
import { buildFillRateDifferenceUrl } from '../../poc/PurchaseOrder/helpers/fillRateMetrics'
import {
  getMetricTableChildCellSx,
  getMetricTableChildRowSx,
  getMetricTableHeadCellSx,
  getMetricTableParentCellSx,
  getMetricTableParentRowSx,
  getMetricTableSubtotalCellSx,
  getMetricTableTotalCellSx,
  METRIC_TABLE_EMPTY_CELL_SX,
  METRIC_TABLE_INFO_ICON_COLOR,
  METRIC_TABLE_INFO_ICON_WRAPPER_STYLE,
  METRIC_TABLE_PAPER_SX,
  METRIC_TABLE_SUBTOTAL_ROW_SX,
  METRIC_TABLE_TOTAL_ROW_SX
} from './style/metricTableStyles'
import BuyerCellContent from './components/BuyerCellContent'
import DifferenceBadge from './components/DifferenceBadge'
import {
  getChildDifferenceAmount,
  getParentDifferenceAmount,
  hasChildDifference
} from './helpers/metricTableDifferences'

const Metricstable = ({ summary = [], totals = {}, startDate, endDate }) => {
  const [openRow, setOpenRow] = useState(null)

  const toggleRow = buyer => {
    setOpenRow(prev => (prev === buyer ? null : buyer))
  }

  const openDifferenceDetails = ({ buyer, productGroup }) => {
    window.open(
      buildFillRateDifferenceUrl({
        startDate,
        endDate,
        buyer,
        productGroup
      }),
      '_blank',
      'noopener,noreferrer'
    )
  }

  const renderParentCellValue = (row, col) => {
    if (col.key === 'difference') {
      const amount = getParentDifferenceAmount(row)

      return (
        <DifferenceBadge
          amount={amount}
        />
      )
    }

    return formatTableValue(row[col.key], col.type)
  }

  const renderChildCellValue = (row, child, col) => {
    if (col.key === 'difference') {
      const amount = getChildDifferenceAmount(child)

      return (
        <DifferenceBadge
          amount={amount}
          clickable={amount !== 0}
          label={`Ver productos con diferencias de ${row.buyer} - ${child.name}`}
          onClick={(event) => {
            event.stopPropagation()
            openDifferenceDetails({
              buyer: row.buyer,
              productGroup: child.name
            })
          }}
        />
      )
    }

    return resolveChildValue(child, col)
  }

  const renderTotalCellValue = (col) => {
    if (col.key === 'buyer') return 'TOTAL'

    if (col.key === 'difference') {
      return <DifferenceBadge amount={totals[col.key]} />
    }

    return formatTableValue(totals[col.key], col.type)
  }

  return (
    <Paper elevation={0} sx={METRIC_TABLE_PAPER_SX}>
      <TableContainer>
        <Table size="small">

          <TableHead className="table-header-gray-poc">
            <TableRow>
              {METRICS_TABLE_COLUMNS.map(col => (
                <TableCell
                  key={col.key}
                  align={col.align}
                  sx={getMetricTableHeadCellSx(col)}
                >
                  <strong>{col.label}</strong>

                  {col.tooltip && (
                    <Tooltip
                      title={col.tooltip}
                      arrow
                      placement="top"
                    >
                      <span
                        style={METRIC_TABLE_INFO_ICON_WRAPPER_STYLE}
                      >
                        <GoogleIcon
                          name="info"
                          size={13}
                          color={METRIC_TABLE_INFO_ICON_COLOR}
                          weight={300}
                        />
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {summary.length === 0 && (
              <TableRow>
                <TableCell colSpan={METRICS_TABLE_COLUMNS.length} sx={METRIC_TABLE_EMPTY_CELL_SX}>
                  No hay resultados.
                </TableCell>
              </TableRow>
            )}

            {summary.map(row => {
              const hasChildren =
                Array.isArray(row.children) && row.children.length > 0
              const isOpen = openRow === row.buyer

              return (
                <React.Fragment key={row.buyer}>

                  <TableRow
                    hover={hasChildren}
                    onClick={() => hasChildren && toggleRow(row.buyer)}
                    sx={getMetricTableParentRowSx({
                      hasChildren,
                      isOpen
                    })}
                  >
                    {METRICS_TABLE_COLUMNS.map((col, index) => (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        sx={getMetricTableParentCellSx({ col, index })}
                      >
                        {index === 0 ? (
                          <BuyerCellContent
                            hasChildren={hasChildren}
                            isOpen={isOpen}
                            value={renderParentCellValue(row, col)}
                          />
                        ) : (
                          renderParentCellValue(row, col)
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                  {hasChildren && isOpen && row.children.map(child => {
                    const childDifferenceAmount = getChildDifferenceAmount(child)
                    const childHasDifference = hasChildDifference(child)

                    return (
                      <TableRow
                        key={child.name}
                        sx={getMetricTableChildRowSx(
                          childHasDifference ? childDifferenceAmount : 0
                        )}
                      >
                        {METRICS_TABLE_COLUMNS.map((col, index) => (
                          <TableCell
                            key={col.key}
                            align={col.align}
                            sx={getMetricTableChildCellSx({ col, index })}
                          >
                            {renderChildCellValue(row, child, col)}
                          </TableCell>
                        ))}
                      </TableRow>
                    )
                  })}

                  {hasChildren && isOpen && (
                    <TableRow sx={METRIC_TABLE_SUBTOTAL_ROW_SX}>
                      {METRICS_TABLE_COLUMNS.map(col => (
                        <TableCell
                          key={col.key}
                          align={col.align}
                          sx={getMetricTableSubtotalCellSx(col)}
                        >
                          {col.key === 'buyer'
                            ? `Subtotal ${row.buyer}`
                            : renderParentCellValue(row, col)}
                        </TableCell>
                      ))}
                    </TableRow>
                  )}

                </React.Fragment>
              )
            })}

            {summary.length > 0 && (
              <TableRow sx={METRIC_TABLE_TOTAL_ROW_SX}>
                {METRICS_TABLE_COLUMNS.map(col => (
                  <TableCell
                    key={col.key}
                    align={col.align}
                    sx={getMetricTableTotalCellSx(col)}
                  >
                    {renderTotalCellValue(col)}
                  </TableCell>
                ))}
              </TableRow>
            )}

          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  )
}

export default Metricstable
